import { Link, NavLink, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { brand } from "../data/mock";

const SCROLL_THRESHOLD_PX = 40;
const MENU_ICON_SIZE = 22;

const nav = [
  { to: "/about", label: "About" },
  { to: "/weddings", label: "Weddings" },
  { to: "/events", label: "Events" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const solid = scrolled || open || !isHome;

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${
        solid
          ? "bg-[hsl(var(--bg))]/95 backdrop-blur border-b border-[hsl(var(--line))] text-[hsl(var(--ink))]"
          : "bg-transparent text-white"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          className="md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={MENU_ICON_SIZE} /> : <Menu size={MENU_ICON_SIZE} />}
        </button>

        <nav className="hidden md:flex items-center gap-9 text-[12px] tracking-[0.22em] uppercase">
          {nav.slice(0, 3).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `link-underline hover:opacity-70 transition-opacity ${isActive ? "opacity-70" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <Link to="/" className="flex items-center gap-3 font-editorial leading-none">
          <img
            src="https://customer-assets-39nsmqrw.emergentagent.net/job_elegant-event-design-1/artifacts/v1a0y2ji_Shobhawithshubhra-logo-final.png"
            alt="Shobha with Shubhra logo"
            className="h-12 md:h-14 w-auto object-contain"
          />
          <span className="text-left">
            <span className={`block text-[10px] tracking-[0.5em] uppercase ${solid ? "text-[hsl(var(--ink-soft))]" : "text-white/80"}`}>Shobha with</span>
            <span className="block text-2xl md:text-3xl tracking-wider">Shubhra</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-9 text-[12px] tracking-[0.22em] uppercase">
          {nav.slice(3).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `link-underline hover:opacity-70 transition-opacity ${isActive ? "opacity-70" : ""}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <ShoppingBag size={18} />
          <span className="text-xs tracking-[0.2em]">0</span>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[520px]" : "max-h-0"
        }`}
      >
        <div className="px-8 pb-8 pt-2 flex flex-col gap-5 text-[13px] tracking-[0.25em] uppercase border-t border-[hsl(var(--line))]">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} className="py-1">
              {n.label}
            </NavLink>
          ))}
          <a href={`mailto:${brand.email}`} className="text-[hsl(var(--accent))] mt-2 normal-case tracking-normal font-display text-xl">
            {brand.email}
          </a>
        </div>
      </div>
    </header>
  );
}
